import React, { useState, useEffect } from 'react';
import { Sun, Moon, Camera, Upload, MessageCircle, Eye, ChevronLeft, ChevronRight, Edit, Trash } from 'lucide-react';
import { useAuth } from '../context/UnifiedAuthContext';
import apiClient from '../utils/apiClient';
import { API_BASE_URL } from '../utils/apiConfig';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Modal from '../components/Modal';
import { Skeleton } from '../components/ui';

const GalleryPage = () => {
  const { isAuthenticated, hasRole, user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, limit: 20, total: 0 });
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionValue, setCaptionValue] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize dark mode from localStorage/system preference
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDarkMode(stored === 'dark');
    } else {
      try {
        const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
        if (mediaQuery?.matches) {
          setIsDarkMode(true);
        }
      } catch {
        // Fallback for test environments
      }
    }
  }, []);

  // Sync dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Helper to get the correct image URL (handles both Supabase URLs and legacy paths)
  const getGalleryImageUrl = (photo) => {
    const candidate = photo.optimized_url || photo.thumbnail_url || photo.filename;
    if (!candidate) return '';
    // If it's already a full URL (Supabase), use it directly
    if (candidate.startsWith('http')) {
      return candidate;
    }
    // Legacy: construct URL from API base
    return `${API_BASE_URL}/gallery/image/${candidate}`;
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchPhotos(page);
    }
  }, [isAuthenticated, page]);

  // Keyboard navigation for photo modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen || photos.length <= 1) return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigatePhoto('prev');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigatePhoto('next');
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, selectedPhotoIndex, photos.length]);

  const fetchPhotos = async (pageNumber = 1) => {
    try {
      const response = await apiClient.get('/gallery', {
        params: { page: pageNumber, limit: meta.limit || 20 }
      });
      const payload = response.data || response;
      const items = payload.data || payload; // standardized vs legacy
      setPhotos(items);
      if (payload.meta) {
        setMeta(payload.meta);
      } else {
        setMeta((prev) => ({ ...prev, total: items.length, totalPages: 1, page: pageNumber }));
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files.length) return;

    setUploading(true);
    console.log('Starting upload process...');
    
    // Validate file sizes and types before upload
    const validFiles = [];
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const SUPPORTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    for (const file of Array.from(files)) {
      console.log(`Validating file: ${file.name}, size: ${file.size}, type: ${file.type}`);
      
      if (!SUPPORTED_TYPES.includes(file.type.toLowerCase())) {
        alert(`File "${file.name}" is not a supported image type. Supported types: JPG, PNG, GIF, WebP`);
        setUploading(false);
        return;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 10MB.`);
        setUploading(false);
        return;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) {
      console.log('No valid files to upload');
      setUploading(false);
      return;
    }

    const formData = new FormData();
    validFiles.forEach(file => {
      formData.append('photos', file);
      console.log(`Added file to FormData: ${file.name}`);
    });

    try {
      console.log('API_BASE_URL:', API_BASE_URL);
      console.log('NODE_ENV:', process.env.NODE_ENV);
      console.log('REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
      
      const uploadUrl = `${API_BASE_URL}/gallery/upload`;
      console.log('Full upload URL:', uploadUrl);
      console.log('Making POST request to:', uploadUrl);
      console.log('FormData entries:', Array.from(formData.entries()).map(([key, value]) => 
        `${key}: ${value instanceof File ? `File(${value.name}, ${value.size} bytes)` : value}`
      ));
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Upload result:', result);
        await fetchPhotos();
        closeUploadModal();
        alert(`Successfully uploaded ${validFiles.length} photo(s)`);
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('Upload failed with status:', response.status, 'Error:', errorText);
        alert(`Upload failed: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Upload error details:', {
        message: error.message,
        stack: error.stack,
        type: error.constructor.name
      });
      alert('Error uploading photos: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const fetchComments = async (photoId) => {
    setLoadingComments(true);
    try {
      const response = await fetch(`${API_BASE_URL}/gallery/${photoId}/comments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const commentsData = await response.json();
        setComments(commentsData);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !selectedPhoto) return;

    try {
      const response = await fetch(`${API_BASE_URL}/gallery/${selectedPhoto.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ comment: newComment }),
        credentials: 'include'
      });

      if (response.ok) {
        const comment = await response.json();
        setComments([...comments, comment]);
        setNewComment('');
        // Refresh photos to update comment counts
        fetchPhotos();
      } else {
        alert('Error adding comment. Please try again.');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment. Please try again.');
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/gallery/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setComments(comments.filter(comment => comment.id !== commentId));
        // Refresh photos to update comment counts
        fetchPhotos();
      } else {
        alert('Error deleting comment. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Error deleting comment. Please try again.');
    }
  };

  const openPhotoModal = (photo) => {
    const photoIndex = photos.findIndex(p => p.id === photo.id);
    setSelectedPhoto(photo);
    setSelectedPhotoIndex(photoIndex);
    setIsModalOpen(true);
    fetchComments(photo.id);
  };

  const closePhotoModal = () => {
    setIsModalOpen(false);
    setSelectedPhoto(null);
    setComments([]);
    setNewComment('');
    setEditingCaption(false);
    setCaptionValue('');
  };

  const navigatePhoto = (direction) => {
    const newIndex = direction === 'next' 
      ? (selectedPhotoIndex + 1) % photos.length 
      : (selectedPhotoIndex - 1 + photos.length) % photos.length;
    
    setSelectedPhotoIndex(newIndex);
    setSelectedPhoto(photos[newIndex]);
    setComments([]);
    setNewComment('');
    setEditingCaption(false);
    setCaptionValue('');
    fetchComments(photos[newIndex].id);
  };

  const openUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    setDragOver(false);
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/gallery/${photoId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setPhotos(photos.filter(photo => photo.id !== photoId));
        closePhotoModal();
      } else {
        alert('Error deleting photo. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Error deleting photo. Please try again.');
    }
  };

  const updateCaption = async () => {
    if (!selectedPhoto) return;

    try {
      const response = await fetch(`${API_BASE_URL}/gallery/${selectedPhoto.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ caption: captionValue }),
        credentials: 'include'
      });

      if (response.ok) {
        const updatedPhoto = await response.json();
        // Update the photo in the photos array
        setPhotos(photos.map(photo => 
          photo.id === selectedPhoto.id ? updatedPhoto : photo
        ));
        // Update the selected photo
        setSelectedPhoto(updatedPhoto);
        setEditingCaption(false);
      } else {
        alert('Error updating caption. Please try again.');
      }
    } catch (error) {
      console.error('Error updating caption:', error);
      alert('Error updating caption. Please try again.');
    }
  };

  const startEditingCaption = () => {
    setCaptionValue(selectedPhoto?.caption || '');
    setEditingCaption(true);
  };

  const cancelEditingCaption = () => {
    setEditingCaption(false);
    setCaptionValue('');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
        <Navbar />
        <div className="flex items-center justify-center min-h-[70vh]">
          <div className="bg-white dark:bg-navy-900 p-12 rounded-2xl shadow-xl max-w-md text-center border border-gray-200 dark:border-navy-800">
            <div className="mb-6">
              <Camera className="w-16 h-16 text-gray-400 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Gallery Access Required</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Please log in to view and upload photos to the league gallery.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-forest-600 text-white px-6 py-3 rounded-lg hover:bg-forest-700 transition-colors"
            >
              Log In
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Header Section */}
        <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                    Photo Gallery
                  </h1>
                  <p className="text-gray-600 dark:text-navy-400 mt-1">
                    Share your golf moments with the league
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Dark Mode Toggle */}
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="
                    flex items-center gap-2 px-4 py-2 rounded-full
                    bg-gray-100 dark:bg-navy-800
                    text-gray-700 dark:text-gray-200
                    hover:bg-gray-200 dark:hover:bg-navy-700
                    transition-colors duration-200
                    border border-gray-200 dark:border-navy-700
                  "
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? (
                    <>
                      <Sun className="w-5 h-5 text-gold-400" />
                      <span className="text-sm font-medium hidden sm:inline">Light</span>
                    </>
                  ) : (
                    <>
                      <Moon className="w-5 h-5 text-navy-600" />
                      <span className="text-sm font-medium hidden sm:inline">Dark</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Stats/Info Row */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-forest-50 dark:bg-forest-900/30 border border-forest-200 dark:border-forest-800">
                <Camera className="w-5 h-5 text-forest-600 dark:text-forest-400" />
                <span className="text-sm font-semibold text-forest-700 dark:text-forest-300">
                  {meta.total || photos.length} Photo{(meta.total || photos.length) !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Upload Button */}
              <button
                onClick={openUploadModal}
                className="
                  flex items-center gap-2 px-4 py-2 rounded-xl
                  bg-forest-600 text-white
                  hover:bg-forest-700
                  transition-colors duration-200
                  font-medium text-sm shadow-md
                "
              >
                <Upload className="w-5 h-5" />
                <span>Upload Photos</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Pagination Controls */}
          {meta.totalPages > 1 && (
            <div className="flex items-center justify-between mb-6 px-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {meta.page || page} of {meta.totalPages || 1}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={(page || 1) <= 1}
                  className="
                    flex items-center gap-1 px-3 py-1.5 rounded-lg
                    bg-white dark:bg-navy-800
                    text-gray-700 dark:text-gray-200
                    border border-gray-200 dark:border-navy-700
                    hover:border-forest-400 dark:hover:border-forest-600
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors duration-200 text-sm font-medium
                  "
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => (meta.totalPages ? Math.min(meta.totalPages, p + 1) : p + 1))}
                  disabled={meta.totalPages ? page >= meta.totalPages : false}
                  className="
                    flex items-center gap-1 px-3 py-1.5 rounded-lg
                    bg-white dark:bg-navy-800
                    text-gray-700 dark:text-gray-200
                    border border-gray-200 dark:border-navy-700
                    hover:border-forest-400 dark:hover:border-forest-600
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors duration-200 text-sm font-medium
                  "
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Photos Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, idx) => (
                <div key={idx} className="bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 rounded-xl shadow-md p-4 space-y-3">
                  <Skeleton className="w-full h-44 rounded-lg" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))}
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">No photos yet</h3>
              <p className="text-gray-500 dark:text-gray-500">Be the first to share a photo with the league!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="
                    group relative bg-white dark:bg-navy-900
                    border border-gray-200 dark:border-navy-800
                    rounded-xl shadow-md overflow-hidden
                    hover:shadow-xl hover:border-forest-400 dark:hover:border-forest-600
                    transition-all duration-300 cursor-pointer
                  "
                  onClick={() => openPhotoModal(photo)}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={getGalleryImageUrl(photo)}
                      alt={photo.caption || 'Gallery photo'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>

                  {/* Comment count badge */}
                  {photo.comment_count > 0 && (
                    <div className="absolute top-2 right-2 bg-forest-600 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1 shadow-lg">
                      <MessageCircle className="w-3 h-3" />
                      <span>{photo.comment_count}</span>
                    </div>
                  )}

                  <div className="p-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 line-clamp-1">
                      {photo.caption || 'No caption'}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-500">
                      <span>By {photo.uploaded_by_name || 'Unknown'}</span>
                      <span>{new Date(photo.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center pointer-events-none">
                    <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Photo Count Footer */}
          {photos.length > 0 && (
            <div className="mt-6 text-center text-sm text-gray-500 dark:text-navy-400">
              Showing {photos.length} photo{photos.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      <Modal isOpen={isUploadModalOpen} onClose={closeUploadModal} title="Upload Photos">
        <div className="max-w-lg mx-auto">
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
              dragOver
                ? 'border-forest-500 bg-forest-50 dark:bg-forest-900/20'
                : 'border-gray-300 dark:border-navy-700 hover:border-forest-400 dark:hover:border-forest-600'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="mb-4">
              <Upload className="w-10 h-10 text-gray-400 dark:text-gray-500 mx-auto" />
            </div>
            <p className="text-lg font-medium text-gray-700 dark:text-gray-200 mb-2">
              {uploading ? 'Uploading photos...' : 'Drop photos here or click to upload'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Supports JPG, PNG, GIF up to 10MB each
            </p>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
              id="file-upload-modal"
              disabled={uploading}
            />
            <label
              htmlFor="file-upload-modal"
              className={`inline-block px-6 py-3 bg-forest-600 text-white rounded-lg cursor-pointer transition-colors ${
                uploading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-forest-700'
              }`}
            >
              {uploading ? 'Uploading...' : 'Choose Files'}
            </label>
          </div>
        </div>
      </Modal>

      {/* Photo Details Modal */}
      <Modal isOpen={isModalOpen} onClose={closePhotoModal}>
        {selectedPhoto && (
          <div className="relative">
            {/* Navigation Arrows */}
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => navigatePhoto('prev')}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-opacity"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => navigatePhoto('next')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-opacity"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Main Image */}
            <div className="mb-4">
              <img
                src={getGalleryImageUrl(selectedPhoto)}
                alt={selectedPhoto.caption || 'Gallery photo'}
                className="w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>

            {/* Photo Info */}
            <div className="bg-gray-50 dark:bg-navy-800 p-4 rounded-lg mb-4">
              <div className="flex items-center justify-between mb-2">
                {editingCaption ? (
                  <div className="flex-1 flex items-center space-x-2">
                    <input
                      type="text"
                      value={captionValue}
                      onChange={(e) => setCaptionValue(e.target.value)}
                      placeholder="Enter caption..."
                      className="flex-1 px-3 py-1 border border-gray-300 dark:border-navy-600 rounded focus:outline-none focus:ring-2 focus:ring-forest-500 bg-white dark:bg-navy-900 text-gray-800 dark:text-gray-200"
                      onKeyPress={(e) => e.key === 'Enter' && updateCaption()}
                      autoFocus
                    />
                    <button
                      onClick={updateCaption}
                      className="px-3 py-1 bg-forest-600 text-white rounded hover:bg-forest-700 transition-colors text-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEditingCaption}
                      className="px-3 py-1 bg-gray-300 dark:bg-navy-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-navy-600 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 flex-1">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex-1">
                      {selectedPhoto.caption || 'No caption'}
                    </h3>
                    {(selectedPhoto.uploaded_by === user?.id || hasRole('admin')) && (
                      <button
                        onClick={startEditingCaption}
                        className="text-forest-600 hover:text-forest-800 dark:text-forest-400 dark:hover:text-forest-300 transition-colors"
                        title="Edit caption"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                {photos.length > 1 && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                    {selectedPhotoIndex + 1} of {photos.length}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                <span>Uploaded by {selectedPhoto.uploaded_by_name || 'Unknown'}</span>
                <span>{new Date(selectedPhoto.created_at).toLocaleString()}</span>
              </div>
            </div>

            {/* Comments Section */}
            <div className="bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Comments</h4>

              {/* Comments List */}
              <div className="max-h-40 overflow-y-auto mb-4">
                {loadingComments ? (
                  <div className="space-y-3">
                    {[1, 2].map((id) => (
                      <div key={id} className="bg-gray-50 dark:bg-navy-800/60 p-3 rounded-lg space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                    ))}
                  </div>
                ) : comments.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No comments yet</p>
                ) : (
                  <div className="space-y-3">
                    {comments.map((comment) => (
                      <div key={comment.id} className="bg-gray-50 dark:bg-navy-800 p-3 rounded-lg">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-sm text-gray-700 dark:text-gray-200">
                            {comment.display_name || comment.username}
                          </span>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(comment.created_at).toLocaleString()}
                            </span>
                            {hasRole('admin') && (
                              <button
                                onClick={() => deleteComment(comment.id)}
                                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                title="Delete comment"
                              >
                                <Trash className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-800 dark:text-gray-200">{comment.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Comment */}
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-500 text-sm bg-white dark:bg-navy-800 text-gray-800 dark:text-gray-200"
                  onKeyPress={(e) => e.key === 'Enter' && addComment()}
                />
                <button
                  onClick={addComment}
                  disabled={!newComment.trim()}
                  className="px-4 py-2 bg-forest-600 text-white rounded-lg hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  Post
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex justify-end space-x-3">
              {hasRole('admin') && (
                <button
                  onClick={() => deletePhoto(selectedPhoto.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Photo
                </button>
              )}
              <button
                onClick={closePhotoModal}
                className="px-4 py-2 bg-gray-300 dark:bg-navy-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-navy-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Footer />
    </div>
  );
};

export default GalleryPage;
